import React, { useContext, useState } from 'react';
import { Row, Nav } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { TfiCheck } from 'react-icons/tfi';
import PropTypes from 'prop-types';
import { CurrencyContext } from '../../utils/currencyContext';
import { EmailPlanContext } from '../../utils/emailPlanContext';
import getCurrencySign from '../../utils/getCurrencySign';
import TableCollapse from '../tableCollapse';
import { emailPlans } from '../../resources/static/index';

function ExtendedPlanColumns({ period }) {
    const { currentCurrency } = useContext(CurrencyContext);
    const { i18n, t } = useTranslation();
    const [openSections, setOpenSections] = useState({});
    const currentLanguage = i18n.language;
    const { currentEmailPlan } = useContext(EmailPlanContext);
    const isFR = currentLanguage === 'fr';

    const toggleCollapse = (id) => {
        setOpenSections((prevSections) => ({
            ...prevSections,
            [id]: !prevSections[id]
        }));
    };
    const getBillingCycle = (currentPlan) => {
        let currencyCode = 'annually';

        if (currentPlan === 'two') {
            currencyCode = 'biennially';
        } else if (currentPlan === 'three') {
            currencyCode = 'triennially';
        }

        return currencyCode;
    };
    const getCurrencyCode = (currency) => {
        let currencyCode = 1;

        if (currency === 'usd') {
            currencyCode = 4;
        } else if (currency === 'gbp') {
            currencyCode = 5;
        }

        return currencyCode;
    };
    const roundToTwo = (num) => parseFloat(num.toFixed(2));
    const sign = getCurrencySign(currentCurrency);
    const dataCurrency = emailPlans[currentCurrency];
    const data = dataCurrency[`year_${period}`];
    const isNotFirstPeriod = period > 1;
    const yearCoefficient = 12 * period;
    const green = '#00af60';

    return (
        <Row>
            <table width="100%" className="extended-plan-table" id="extended-plan-table">
                <thead>
                    <tr>
                        {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                        <th />
                        <th className="plan-card">
                            <h2 className="plan-title">MailPro</h2>
                            <div className="plan-price">
                                <p className="price-info">
                                    <span className="price-home">
                                        {data.mailpro}
                                        <sup>{sign}</sup>
                                    </span>
                                    /{t('titles.month')}
                                </p>
                                <p className="price-year">
                                    {isNotFirstPeriod && (
                                        <del>
                                            {roundToTwo(
                                                dataCurrency.year_1.mailpro * yearCoefficient
                                            )}
                                            {sign}
                                        </del>
                                    )}
                                    &nbsp;
                                    <strong>
                                        {isNotFirstPeriod
                                            ? roundToTwo(data.mailpro * yearCoefficient)
                                            : roundToTwo(
                                                  dataCurrency.year_1.mailpro * yearCoefficient
                                              )}
                                        {sign}
                                    </strong>
                                    &nbsp;{t('titles.for')} {period}{' '}
                                    {period > 1 ? t('titles.years') : t('titles.year')}
                                </p>
                            </div>
                            <Nav className="common-nav-button yellow">
                                <Nav.Link
                                    className="navbar-brand"
                                    href={`https://obambu.com/order/?pid=149&language=${isFR ? 'french' : 'english'}&currency=${getCurrencyCode(currentCurrency)}&billingcycle=${getBillingCycle(currentEmailPlan)}`}
                                    rel="noopener noreferrer"
                                >
                                    {t('buttons.order-now')}
                                </Nav.Link>
                            </Nav>
                        </th>
                        <th className="plan-card">
                            <h2 className="plan-title">
                                MailPro <span>Plus+</span>
                            </h2>
                            <div className="plan-price">
                                <p className="price-info">
                                    <span className="price-home">
                                        {data.mailpro_plus}
                                        <sup>{sign}</sup>
                                    </span>
                                    /{t('titles.month')}
                                </p>
                                <p className="price-year">
                                    {isNotFirstPeriod && (
                                        <del>
                                            {roundToTwo(
                                                dataCurrency.year_1.mailpro_plus * yearCoefficient
                                            )}
                                            {sign}
                                        </del>
                                    )}
                                    &nbsp;
                                    <strong>
                                        {isNotFirstPeriod
                                            ? roundToTwo(data.mailpro_plus * yearCoefficient)
                                            : roundToTwo(
                                                  dataCurrency.year_1.mailpro_plus * yearCoefficient
                                              )}
                                        {sign}
                                    </strong>
                                    &nbsp;{t('titles.for')} {period}{' '}
                                    {period > 1 ? t('titles.years') : t('titles.year')}
                                </p>
                            </div>
                            <Nav className="common-nav-button yellow">
                                <Nav.Link
                                    className="navbar-brand"
                                    href={`https://obambu.com/order/?pid=150&language=${isFR ? 'french' : 'english'}&currency=${getCurrencyCode(currentCurrency)}&billingcycle=${getBillingCycle(currentEmailPlan)}`}
                                    rel="noopener noreferrer"
                                >
                                    {t('buttons.order-now')}
                                </Nav.Link>
                            </Nav>
                        </th>
                    </tr>
                </thead>
                <tbody className="card-header">
                    <tr>
                        <th colSpan="5" className="sub-heading">
                            {t('email-page.ext-plans-column.table-1.title')}
                        </th>
                    </tr>
                </tbody>
                <tbody>
                    <tr>
                        <th>{t('email-page.ext-plans-column.table-1.property-1')}</th>
                        <td>5</td>
                        <td>{t('titles.unlimited')}</td>
                    </tr>
                    <tr>
                        <th>{t('email-page.ext-plans-column.table-1.property-2')}</th>
                        <td>15{t('web-hosting-page.ext-plans-column.gb')}</td>
                        <td>50{t('web-hosting-page.ext-plans-column.gb')}</td>
                    </tr>
                    <tr>
                        <th>{t('email-page.ext-plans-column.table-1.property-3')}</th>
                        <td>30</td>
                        <td>{t('titles.unlimited')}</td>
                    </tr>
                    <tr>
                        <th>{t('email-page.ext-plans-column.table-1.property-4')}</th>
                        <td>5</td>
                        <td>{t('titles.unlimited')}</td>
                    </tr>
                    <tr>
                        <th>{t('email-page.ext-plans-column.table-1.property-5')}</th>
                        <td>{t('web-hosting-page.ext-plans-column.weekly')}</td>
                        <td>{t('web-hosting-page.ext-plans-column.daily')}</td>
                    </tr>
                    <tr>
                        <th>{t('email-page.ext-plans-column.table-1.property-6')}</th>
                        <td>{t('titles.unlimited')}</td>
                        <td>{t('titles.unlimited')}</td>
                    </tr>
                    <tr>
                        <th>{t('email-page.ext-plans-column.table-1.property-7')}</th>
                        <td>{t('web-hosting-page.ext-plans-column.included')}</td>
                        <td>{t('web-hosting-page.ext-plans-column.included')}</td>
                    </tr>
                    <tr>
                        <th>{t('email-page.ext-plans-column.table-1.property-8')}</th>
                        <td>
                            {' '}
                            <TfiCheck color={green} />
                        </td>
                        <td>
                            {' '}
                            <TfiCheck color={green} />
                        </td>
                    </tr>
                    <tr>
                        <th>{t('email-page.ext-plans-column.table-1.property-9')}</th>
                        <td>
                            {' '}
                            <TfiCheck color={green} />
                        </td>
                        <td>
                            {' '}
                            <TfiCheck color={green} />
                        </td>
                    </tr>
                </tbody>
            </table>
            <div className="table-wrapper">
                <TableCollapse
                    isOpen={!!openSections.section1}
                    toggle={toggleCollapse}
                    id="section1"
                    title={t('email-page.ext-plans-column.table-2.title')}
                >
                    <table className="pricing-table">
                        <tbody>
                            <tr>
                                <th>{t('email-page.ext-plans-column.table-2.property-1')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('email-page.ext-plans-column.table-2.property-2')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('email-page.ext-plans-column.table-2.property-3')}</th>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited')}</td>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited')}</td>
                            </tr>
                            <tr>
                                <th>{t('email-page.ext-plans-column.table-2.property-4')}</th>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited')}</td>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited')}</td>
                            </tr>
                            <tr>
                                <th>{t('email-page.ext-plans-column.table-2.property-5')}</th>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited')}</td>
                                <td>{t('web-hosting-page.ext-plans-column.unlimited')}</td>
                            </tr>
                            <tr>
                                <th>{t('email-page.ext-plans-column.table-2.property-6')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('email-page.ext-plans-column.table-2.property-7')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </TableCollapse>
            </div>
            <div className="table-wrapper">
                <TableCollapse
                    isOpen={!!openSections.section2}
                    toggle={toggleCollapse}
                    id="section2"
                    title={t('email-page.ext-plans-column.table-3.title')}
                >
                    <table className="pricing-table">
                        <tbody>
                            <tr>
                                <th>{t('email-page.ext-plans-column.table-3.property-1')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('email-page.ext-plans-column.table-3.property-2')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('email-page.ext-plans-column.table-3.property-3')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('email-page.ext-plans-column.table-3.property-4')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('email-page.ext-plans-column.table-3.property-5')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </TableCollapse>
            </div>
            <div className="table-wrapper">
                <TableCollapse
                    isOpen={!!openSections.section3}
                    toggle={toggleCollapse}
                    id="section3"
                    title={t('email-page.ext-plans-column.table-4.title')}
                >
                    <table className="pricing-table">
                        <tbody>
                            <tr>
                                <th>{t('email-page.ext-plans-column.table-4.property-1')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('email-page.ext-plans-column.table-4.property-2')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('email-page.ext-plans-column.table-4.property-3')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </TableCollapse>
            </div>
            <div className="table-wrapper">
                <TableCollapse
                    isOpen={!!openSections.section4}
                    toggle={toggleCollapse}
                    id="section4"
                    title={t('email-page.ext-plans-column.table-5.title')}
                >
                    <table className="pricing-table">
                        <tbody>
                            <tr>
                                <th>{t('email-page.ext-plans-column.table-5.property-1')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('email-page.ext-plans-column.table-5.property-2')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('email-page.ext-plans-column.table-5.property-3')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('email-page.ext-plans-column.table-5.property-4')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                            <tr>
                                <th>{t('email-page.ext-plans-column.table-5.property-5')}</th>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                                <td>
                                    {' '}
                                    <TfiCheck color={green} />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </TableCollapse>
            </div>
        </Row>
    );
}

ExtendedPlanColumns.propTypes = {
    period: PropTypes.number
};

ExtendedPlanColumns.defaultProps = {
    period: 1
};

export default ExtendedPlanColumns;
